import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { LiveState, GazeSample } from "../api/types";
import { useApp } from "../state/AppContext";
import { Badge, ProgressBar } from "../components/ui";

function Preview({ role, connected, gaze }: { role: string; connected: boolean; gaze?: GazeSample }) {
  const ref = useRef<HTMLImageElement>(null);
  const [fresh, setFresh] = useState(false);
  const [aspect, setAspect] = useState(16/9);
  useEffect(() => {
    if (!connected) { setFresh(false); return; }
    let stopped = false, timer = 0, url = "";
    const controller = new AbortController();
    const poll = async () => {
      try {
        const response = await fetch(`/api/v1/live/preview/${role}.jpg`, { signal: controller.signal });
        if (!response.ok) throw Error("No frame");
        const blob = await response.blob();
        if (stopped) return;
        URL.revokeObjectURL(url); url = URL.createObjectURL(blob);
        if (ref.current) ref.current.src = url;
        setFresh(true);
      } catch { if (!stopped) setFresh(false); }
      if (!stopped) timer = window.setTimeout(poll, 500);
    };
    void poll();
    return () => { stopped = true; clearTimeout(timer); controller.abort(); URL.revokeObjectURL(url); };
  }, [connected, role]);
  return <div className="live-eye" style={{aspectRatio:aspect}}><img ref={ref} alt={`${role} camera preview`} style={{ opacity: fresh ? 1 : .15, visibility: fresh ? "visible" : "hidden" }} onLoad={() => {if(ref.current?.naturalHeight) setAspect(ref.current.naturalWidth/ref.current.naturalHeight);}} />
    {fresh && gaze?.valid && gaze.x != null && gaze.y != null && <i className="live-gaze" style={{left:`${gaze.x*100}%`,top:`${gaze.y*100}%`}} />}
    {!fresh && <span className="live-wait">{connected ? "Waiting for a recent frame" : "Connect a recording phone"}</span>}
    <span className="live-eye-label">{role.toUpperCase()} · PREVIEW</span></div>;
}

export function LiveMonitor() {
  const { notify } = useApp();
  const [address, setAddress] = useState(() => new URLSearchParams(location.search).get("device") || "http://argus.local:8080");
  const [state, setState] = useState<LiveState | null>(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(true);
  const [analyze, setAnalyze] = useState(true);
  const [devices, setDevices] = useState<{ name: string; address: string }[]>([]);
  useEffect(() => {
    let stopped = false, timer = 0;
    const poll = async () => {
      try { const value = await api.live(); if (!stopped) setState(value); }
      catch { if (!stopped) setState(null); }
      if (!stopped) timer = window.setTimeout(poll, 1000);
    };
    void poll(); return () => { stopped = true; clearTimeout(timer); };
  }, []);
  const connect = async () => {
    setBusy(true);
    try { setState(await api.connect(address, auto, analyze)); }
    catch (e: any) { notify(e.message, "error"); }
    finally { setBusy(false); }
  };
  const discover = async () => {
    setBusy(true);
    try { const result = await api.discover(); setDevices(result.devices); if (!result.devices.length) notify(result.reason || "No phones found. Use the IP address shown on the phone."); }
    catch (e: any) { notify(e.message, "error"); }
    finally { setBusy(false); }
  };
  const gaze = state?.device?.gaze;
  return <div className="live-workspace">
    <header className="live-heading"><div><div className="tiny muted">CAPTURE → VERIFY → ANALYZE</div><h1>Live capture</h1>
      <p className="muted">Preview a phone on the same Wi-Fi and automatically import originals after recording.</p></div>
      <Badge kind={state?.connected ? "good" : "neutral"}>{state?.connected ? "Connected · local" : "Offline"}</Badge></header>
    <div className="panel pad live-connect">
      <div className="row"><input aria-label="Phone address" value={address} onChange={e => setAddress(e.target.value)} style={{flex:1}} onKeyDown={e => {if(e.key === "Enter") void connect();}} />
        <button disabled={busy} onClick={discover}>Find phones</button><button disabled={busy} className="primary" onClick={connect}>Connect</button>
        {state?.address && <button onClick={() => api.disconnect().then(() => api.live()).then(setState)}>Disconnect</button>}</div>
      <div className="row" style={{marginTop:12, gap:24}}><label><input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} /> Auto-import finished recordings</label>
        <label><input type="checkbox" checked={analyze} onChange={e => setAnalyze(e.target.checked)} /> Analyze after import</label>
        <span className="tiny muted">Applied when connecting · AWS upload disabled</span></div>
      {devices.map(d => <button key={d.address} onClick={() => setAddress(d.address)}>{d.name} · {d.address}</button>)}
      {state?.error && <div className="error-box" style={{marginTop:10}}>{state.error} · Reconnecting automatically. Check Wi-Fi and keep the phone app open.</div>}
    </div>
    <div className="live-previews">{["left","right"].map(role => <Preview key={role} role={role} connected={!!state?.connected && !!state?.device?.recording_id} gaze={role === "left" ? gaze : undefined} />)}</div>
    <div className="row"><Badge kind="neutral">{state?.device?.model || "USB2Camera"}</Badge><span className="mono">Gaze {gaze?.x?.toFixed(3) ?? "—"}, {gaze?.y?.toFixed(3) ?? "—"}</span>
      <span className="tiny muted">{gaze?.synthetic ? "Synthetic center · not measured eye tracking" : gaze?.source || "Waiting for gaze"} · up to 2 fps preview · original resolution retained on phone</span></div>
    <section className="panel"><div className="panel-head">Recordings <span className="spacer"/><span className="tiny muted">SHA-256 verified · local workspace</span></div>
      <div className="panel-body pad">{!state?.recordings.length && <p className="muted">Start a capture in the phone app. Completed recordings and transfer status appear here.</p>}
        {state?.recordings.map(row => {const job = state.jobs[row.id]; const sid = job?.result?.session_id as string | undefined; return <div className="live-recording" key={row.id}>
          <div><strong>{new Date(row.started_unix_ms).toLocaleString()}</strong><div className="tiny muted mono">{row.id}</div></div>
          <Badge kind={row.state === "ready" ? "good" : "neutral"}>{row.state}</Badge><span className="tiny muted">{(row.artifacts.reduce((sum,a)=>sum+a.size_bytes,0)/1048576).toFixed(1)} MB</span>
          <div style={{flex:1}}>{job && <><div className="tiny">{job.state} · {job.error || job.message}</div><ProgressBar value={job.progress} state={job.state}/></>}</div>
          {sid && job?.state === "succeeded" ? <button onClick={() => {location.hash=`session/${sid}`; location.reload();}}>Review session</button>
            : <button disabled={row.state !== "ready" || (!!job && ["queued","running"].includes(job.state))} onClick={() => api.receive(row.id).catch((e: Error) => notify(e.message,"error"))}>{job?.state === "failed" ? "Retry" : "Import"}</button>}
        </div>;})}</div></section>
  </div>;
}
